import { BookmarksContext } from "context/bookmarks";
import React, { memo, useCallback, useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { getMediaKey } from 'api/media';
import { isTouchDevice } from 'utils/screen';
import { isEbookContentType } from 'utils/assets';
import { Helmet } from 'react-helmet';
import { READER_THEMES } from 'utils/themes';
import { ebookDataPropType, queuePropType } from 'utils/prop-types';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import FullScreenHeader from 'components/full-screen-components/header';
import clsx from 'clsx';
import Fade from '@material-ui/core/Fade';
import useStyles from './pubhub-reader.styles';
import ControlPanel from './components/control-panel';
import EbookTimeline from '../components/ebook-timeline';
import { v4 as uuidv4 } from 'uuid';
import D2Reader from "@d-i-t-a/reader";
import combineURLs from 'axios/lib/helpers/combineURLs';

import {
  saveAnnotationLocation,
  deleteAnnotationLocation,
  updateAnnotationLocation,
  loadHistory,
  loadAnnotations,
} from 'utils/firestore';
import firebase from "utils/auth/firebase";
import { processContent } from "utils/content";
import { extractTransformersUidFromMediaKey } from "api/media/media.utils";
import { ENABLE_READER_INSPECTION } from "constants/env";

const INJECTABLES_CSS_PATH = `${window.location.origin}/epub-reader/injectables/readium-css/`;
const READIUMCSS_BEFORE = `${INJECTABLES_CSS_PATH}/ReadiumCSS-before.css`; // Why is this not used?
const READIUMCSS_DEFAULT = `${INJECTABLES_CSS_PATH}/ReadiumCSS-default.css`;
const READIUMCSS_AFTER = `${INJECTABLES_CSS_PATH}/ReadiumCSS-after.css`;


// env.PUBLIC_URL points at https://pubfront-reader.storage.googleapis.com/ for some reason...
// hacking this for local development but need a fix
const injectables = [
  {
    type: 'style',
    url: READIUMCSS_BEFORE,
    r2before: true,
  },
  {
    type: 'style',
    url: READIUMCSS_DEFAULT,
    r2default: true,
  },
  {
    type: 'style',
    url: READIUMCSS_AFTER,
    r2after: true,
  },
];

const PubhubReader = memo((props) => {

  const {
    queue,
    ebookData: { activeTheme },
    activeIndex,
    resetPlayerAction,
    changeEbookReaderOpenAction,
    updateEBookDataAction,
  } = props;
  const isTouchableDevice = isTouchDevice();
  const activeQueueItem = queue[activeIndex];
  const readerRef = useRef();

  const [isDistractionFreeMode, setIsDistractionFreeMode] = useState(true);
  const [isReaderInstanceSaved, setIsReaderInstanceSaved] = useState(false);
  const [isEBookLoaded, setIsEBookLoaded] = useState(false);
  const [goToPage, setGoToPage] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [annotationRefreshTrigger, setAnnotationRefreshTrigger] = useState(0);

  const handleAnnotationsChanged = useCallback(() => {
    setAnnotationRefreshTrigger(prev => prev + 1);
  }, []);

  const { loadBookmarks, bookmarks } = useContext(BookmarksContext);

  const classes = useStyles({ activeTheme, activeQueueItem, zoomLevel });

  const toggleZoom = useCallback(() => {
    setZoomLevel((current) => {
      if (current == 100) {
        return 200;
      } else if (current == 200) {
        return 300;
      } else {
        return 100;
      }
    });
  }, []);

  const goToPrevPage = useCallback((e) => {
    e.stopPropagation();
    readerRef.current.navigator.previousPage();
  }, []);

  const goToNextPage = useCallback((e) => {
    e.stopPropagation();
    readerRef.current.navigator.nextPage();
  }, []);

  const handleTimelineVisibleRangeChanged = useCallback((location) => {
    const newTotalPages = location.locations.position + location.locations.totalRemainingPositions; //event.totalNumberOfPages;
    const newCurrentPage = location.locations.position; //event.timelineVisibleStartPageIndex;

    updateEBookDataAction({
      totalPages: newTotalPages,
      currentPage: newCurrentPage,
      duration: location.locations.position + location.locations.totalRemainingPositions,
      progress: location.locations.position,
      skipHistoryUpdate: false,
    });
  }, []);

  const closeEbookReader = useCallback(() => {
    changeEbookReaderOpenAction(false);
  }, []);

  const handleClick = useCallback((event) => {

    if (!readerRef.current.highlighter.isSelectionMenuOpen) {
      setIsDistractionFreeMode((current) => !current);
    }
  }, []);

  const waitForReadium = useCallback(() => {
      updateEBookDataAction({ totalPages: 0, currentPage: 1, skipHistoryUpdate: true });
      loadEbookAsset();
  }, []);

  useEffect(() => {
    if (isReaderInstanceSaved) {
      setIsEBookLoaded(true);
    }
  }, [isReaderInstanceSaved]);

  useEffect(() => {
    if (readerRef.current) {
      readerRef.current.navigator.handleResize();
    }
  }, [zoomLevel]);

  useEffect(() => {
    if (readerRef.current && goToPage) {
      readerRef.current.goToPosition(goToPage);
    }
  }, [goToPage]);

  // wait for reader
  useEffect(() => {
    waitForReadium();

    // Clean up
    return () => {
      if (isEBookLoaded) {
        updateEBookDataAction({ totalPages: 0, currentPage: 1, skipHistoryUpdate: true });
        setIsEBookLoaded(false);
      }
    };
  }, []);

  function getReadiumAppearance() {
    switch (activeTheme) {
      case READER_THEMES.dark:
        return 'readium-night-on';
      case READER_THEMES.sepia:
        return 'readium-sepia-on';
      default:
        return 'readium-default-on';
    }
  }

  const getRequestConfig = useCallback((firebaseAuth = false) => {
    return new Promise(async (resolve, reject) => {
      if (firebaseAuth) {
        const token = await firebase?.auth()?.currentUser?.getIdToken?.();
        resolve({
          headers: {
            authorization: `Firebase ${token}`,
          },
        });
      } else {
        resolve({});
      }
    });
  }, [])

  const fetchResource = useCallback(async (href, enableAuthProtection = false) => {
    const requestConfig = await getRequestConfig(enableAuthProtection);
    return new Promise(async (resolve, reject) => {
      if (enableAuthProtection){
        await fetch(href, requestConfig)
            .then((response) => response.arrayBuffer())
            .then(async (buffer) => {
              resolve(await processContent(buffer));
            })
            .catch(e => reject(e))
      } else {
       await fetch(href, requestConfig)
           .then((response) => response.text())
           .then((data) => resolve(data))
      }
    });
  }, [getRequestConfig]);

  const getManifestUrl = useCallback((mediaKey) => {
    return new URL(combineURLs(mediaKey, 'manifest.json'));
  }, []);

  const getManifest = useCallback(async (mediaKey, enableAuthProtection = false) => {
    const manifestUrl = getManifestUrl(mediaKey);
    const data = await fetchResource(manifestUrl.href, enableAuthProtection)
        .catch(e => console.error(e));
    return JSON.parse(data);
  }, [fetchResource, getManifestUrl]);

  const loadEbookAsset = useCallback(() => {
      const { downloadUrl, assetId, contentType } = activeQueueItem ?? {};

      if (downloadUrl && isEbookContentType({ contentType })) {
        getMediaKey(downloadUrl).then(({ key }) => {
          const hasTransformersKey = !!extractTransformersUidFromMediaKey(key);
          const enableAuthProtection = hasTransformersKey;
          const enableSearch = hasTransformersKey;
          return Promise.all([
            loadHistory(assetId),
            loadAnnotations(assetId),
            loadBookmarks(assetId),
            getManifest(key, enableAuthProtection),
            getRequestConfig(enableAuthProtection)
          ])
          .then((results) => {
            const [history, annotations, bookmarks, manifest, requestConfig] = results;
            // get layout from the manifest instead of the asset object since we don't always have the information
            const fixedLayout = (manifest?.metadata?.rendition?.layout || "unknown") === "fixed";
            if (key) {
              D2Reader.load({
                url: getManifestUrl(key),
                publication: manifest,
                requestConfig: requestConfig,
                injectables: injectables,
                injectablesFixed: [],
                initialAnnotations: {
                  bookmarks: bookmarks,
                  highlights: annotations,
                },
                userSettings: {
                  verticalScroll: 'readium-scroll-off',
                  // lineHeight: 1.5,
                  textAlignment: 'auto', // ["auto","justify","start"]
                  columnCount: fixedLayout ? '1' : 'auto', //getReadiumColumnCount(), // fixedLayout ? 'auto' : 'auto', // ["auto", "1", "2"]
                  fontFamily: 'Original', // ["Original","serif","sans-serif"] ["Original", "Helvetica Neue", "Iowan Old Style", "Athelas", "Seravek", "OpenDyslexic", "AccessibleDfA", "IA Writer Duospace"]
                  // fontOverride: "readium-font-off", // [readium-font-off, readium-font-on]
                  advancedSettings: "readium-advanced-off", // [readium-advanced-on, readium-advanced-off]
                  appearance: getReadiumAppearance(), //["readium-default-on", "readium-sepia-on", "readium-night-on"]
                },
                rights: {
                  autoGeneratePositions: true,
                  enableAnnotations: true,
                  enableBookmarks: true,
                  enableContentProtection: !ENABLE_READER_INSPECTION,
                  enableDefinitions: true,
                  enableLineFocus: true,
                  enableMediaOverlays: true,
                  enablePageBreaks: false,
                  enableSearch: enableSearch,
                  enableTTS: true,
                  enableTimeline: true,
                  customKeyboardEvents: false,
                  enableHistory: true,
                  enableCitations: true,
                },
                attributes: {
                  margin: fixedLayout ? 0 : 0,
                  navHeight: fixedLayout ? 0 : 30, // number,
                  iframePaddingTop: fixedLayout ? 0 : 50,
                  bottomInfoHeight: 0, // number,
                  sideNavPosition: 'left', // "left" | "right";
                },
                contentBytesLengthService: fixedLayout
                  ? undefined
                  : function (href, requestConfig) {
                      window.contentBytesLengths = window.contentBytesLengths || [];
                      window.contentBytesLengths[key] =
                        window.contentBytesLengths[key] ||
                        fetchResource(combineURLs(key, 'contentBytesLength.json'), enableAuthProtection).then((data) => JSON.parse(data));

                      return window.contentBytesLengths[key].then((content) => content[href.substring(key.length + 1)]);
                    },
                annotations: {
                  enableComments: true,
                  // initialAnnotationColor: "var(--RS__highlightColor)",
                  initialAnnotationColor: '#ff8500',
                  hideLayer: false,
                  api: {
                    addAnnotation: function (highlight) {
                      highlight.id = uuidv4().toUpperCase();
                      return new Promise(function (resolve, reject) {
                        saveAnnotationLocation(assetId, highlight).then((result) => {
                          handleAnnotationsChanged();
                          resolve(result);
                        }).catch(reject);
                      });
                    },
                    deleteAnnotation: function (highlight) {
                      return new Promise(function (resolve, reject) {
                        deleteAnnotationLocation(assetId, highlight).then((result) => {
                          handleAnnotationsChanged();
                          resolve(result);
                        }).catch(reject);
                      });
                    },
                    selectedAnnotation: function (highlight) {

                      readerRef.current.highlighter.isSelectionMenuOpen = true;
                      return new Promise(function (resolve, reject) {
                        resolve(highlight);
                      });
                    },
                    updateAnnotation: function (highlight) {
                      return new Promise(function (resolve, reject) {
                        updateAnnotationLocation(assetId, highlight).then((result) => {
                          handleAnnotationsChanged();
                          resolve(result);
                        }).catch(reject);
                      });
                    },
                    addCommentToAnnotation: function (highlight) {
                      let h = swal({
                        // text: "Update or Add Note Here:",
                        content: {
                          element: 'textarea',
                          attributes: {
                            value: highlight.highlight.note ?? '',
                          },
                        },
                        buttons: true,
                      }).then((value) => {
                        value = document.querySelector('.swal-content__textarea').value;
                        highlight.highlight.note = value;
                        swal.stopLoading();
                        swal.close();
                        return highlight;
                      });
                      return new Promise(function (resolve, reject) {
                        resolve(h);
                      });
                    },
                  },
                },
                bookmarks: {
                  api: {},
                },
                protection: {
                  enforceSupportedBrowsers: false,
                  enableEncryption: false,  // does nothing right now
                  enableObfuscation: false,
                  disablePrint: false,
                  disableCopy: false,
                  canCopy: true,
                  charactersToCopy: 2500,
                  detectInspect: true,
                  clearOnInspect: true, // make sure detectInspect is true, otherwise this won't work
                  detectInspectInitDelay: 10,
                  disableKeys: true,
                  disableContextMenu: false,
                  hideTargetUrl: false,
                  disableDrag: false,
                  supportedBrowsers: [
                    // this will only be used if enforceSupportedBrowsers is true, and has nothing to do with the r2d2bc modules supported browsers
                    'Chrome',
                    'ChromeAndroid',
                    'Edge',
                    'Firefox',
                    'iOS',
                    'Safari',
                  ],
                  excludeNodes: [],

                  api: {
                    // make sure detectInspect is true, otherwise this won't be called
                    // this callback will be executed in a loop while inspect is visible
                    inspectDetected: function () {
                      console.log('inspect detected');
                    },
                  },
                },
                mediaOverlays: {
                  autoScroll: true,
                  autoTurn: true,
                  // color: "redtext",
                  volume: 0.7,
                  rate: 1.2,
                  wait: 1,
                  // hideLayer:true,
                  api: {},
                },
                api: {
                  getContent: (href) => {
                    return fetchResource(href, enableAuthProtection)
                  },
                  updateCurrentLocation: function (location) {
                    handleTimelineVisibleRangeChanged(location);

                    return new Promise(function (resolve, reject) {
                      resolve(location);
                    });
                  },
                  updateSettings: function (settings) {
                    return new Promise(function (resolve, reject) {
                      resolve(settings);
                    });
                  },
                  resourceReady: function () {
                    if (history?.progress && !goToPage) {
                      setGoToPage(history?.progress);
                    }
                  },
                  onError: function (error) {
                    console.log(error);
                  },
                },
              })
              .then((instance) => {
                instance.navigator.handleClickThrough = handleClick;
                readerRef.current = instance;
                setIsReaderInstanceSaved(true);

              })
              .catch((error) => {
                console.error({ error });
                return swal({
                  text: error.message,
                }).then((value) => {
                  resetPlayerAction();
                });
              });
            }
          })
          .catch((error) => {
            let text = error.response?.data.error ?? error.message;
            return swal({
              text: text,
              focusConfirm: false,
            }).then((value) => {
              resetPlayerAction();
            });
          });
        });
      }

      return () => {
        updateEBookDataAction({ totalPages: 0, currentPage: 1, skipHistoryUpdate: true });
      };
    },
    [activeQueueItem],
  );

  const renderControlPanel = useCallback(
    () =>
      isEBookLoaded ? (
        <ControlPanel
          assetId={activeQueueItem?.assetId}
          readerRef={readerRef}
          activeTheme={activeTheme}
          toggleZoom={toggleZoom}
          isZoomActive={zoomLevel > 200}
          isHeaderActive={!isDistractionFreeMode}
          annotationRefreshTrigger={annotationRefreshTrigger}
        />
      ) : null,
    [activeQueueItem, activeTheme, isEBookLoaded, toggleZoom, zoomLevel, isDistractionFreeMode, bookmarks],
  );

  const handleTimelineChange = useCallback(
    (newPageNumber) => {
      readerRef?.current?.goToPosition?.(newPageNumber);
    },
    [readerRef],
  );

  return (
    <>
      <Helmet>
        {/* <link rel="stylesheet" href="https://cdn.muicss.com/mui-latest/css/mui.min.css" /> */}
        <script src="https://cdn.muicss.com/mui-latest/js/mui.min.js"></script>
        <script src="https://code.jquery.com/jquery-2.1.4.min.js"></script>
        <script src="https://cdnjs.cloudflare.com/ajax/libs/sweetalert/2.1.2/sweetalert.min.js"></script>

        <link rel="stylesheet" type="text/css" href="https://fonts.googleapis.com/icon?family=Material+Icons" />
      </Helmet>
      {activeQueueItem && (
        <Fade in={!isDistractionFreeMode}>
          <Box className={classes.readerHeaderContainer}>
            <FullScreenHeader
              height={99}
              item={activeQueueItem}
              onClose={closeEbookReader}
              nameClassName={classes.textThemeColor}
              creatorClassName={classes.textThemeColor}
              renderControlPanel={renderControlPanel}
            />
          </Box>
        </Fade>
      )}
      <Box className={classes.readerViewContainer}>
        {!isTouchableDevice && (
          <Box className={clsx(classes.arrowBg, classes.leftArrow)}>
            <IconButton onClick={goToPrevPage} className={classes.arrowIconBtn}>
              <ArrowBackIosIcon className={classes.icon} />
            </IconButton>
          </Box>
        )}
        {!isTouchableDevice && (
          <Box className={clsx(classes.arrowBg, classes.rightArrow)}>
            <IconButton onClick={goToNextPage} className={classes.arrowIconBtn}>
              <ArrowForwardIosIcon className={classes.icon} />
            </IconButton>
          </Box>
        )}
        <Box id="D2Reader-Container" className={classes.readerView}>
          <main  id="iframe-wrapper" tabIndex={-1} className={classes.iframeWrapper}>
            <div id="reader-loading" className={clsx(classes.loading, "loading")}></div>
            <div id="reader-error" className="error"></div>
            <div style={{ height: '0px' }}>
              <div id="highlight-toolbox" className="highlight-toolbox">
                <div id="highlight-toolbox-mode-colors">
                  <button style={{ backgroundColor: 'gainsboro' }} id="dismissIcon">
                    <i className="material-icons" style={{ color: 'gray' }}>
                      close
                    </i>
                  </button>
                </div>
                <div id="highlight-toolbox-mode-add">
                  <button style={{ display: 'unset' }} id="colorIcon" className="color-option">
                    <span />
                  </button>
                  <button style={{ backgroundColor: 'gainsboro' }} id="highlightIcon">
                    <span
                      style={{
                        background: 'yellow',
                        display: 'inline-block',
                        width: 24,
                        height: 24,
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width={24}
                        height={24}
                        viewBox="0 0 24 24"
                        preserveAspectRatio="xMidYMid meet"
                        role="img"
                        className="icon open"
                        aria-labelledby="text-icon"
                      >
                        <title id="text-icon">Text</title>
                        <path d="M5 4v3h5.5v12h3V7H19V4z" />
                      </svg>
                    </span>
                  </button>
                  <button style={{ backgroundColor: 'gainsboro' }} id="underlineIcon">
                    <span
                      style={{
                        display: 'inline-block',
                        width: 24,
                        height: 24,
                        borderBottom: 'yellow solid 4px',
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width={24}
                        height={24}
                        viewBox="0 0 24 24"
                        preserveAspectRatio="xMidYMid meet"
                        role="img"
                        className="icon open"
                        aria-labelledby="text-icon"
                      >
                        <title id="text-icon">Text</title>
                        <path d="M5 4v3h5.5v12h3V7H19V4z" />
                      </svg>
                    </span>
                  </button>
                </div>
                <div id="highlight-toolbox-mode-edit">
                  <button style={{ backgroundColor: 'gainsboro', display: 'none' }} id="commentIcon">
                    <i className="material-icons" style={{ color: 'black' }}>
                      insert_comment
                    </i>
                  </button>
                  <button style={{ backgroundColor: 'gainsboro', display: 'none' }} id="deleteIcon">
                    <i className="material-icons" style={{ color: 'black' }}>
                      delete
                    </i>
                  </button>
                </div>
              </div>
            </div>
          </main>
          <div id="container-view-security" className="security" />
        </Box>
      </Box>
      <EbookTimeline
        onChange={handleTimelineChange}
        textClassName={classes.textThemeColor}
        isDistractionFreeMode={isDistractionFreeMode}
        timelineContainerClassName={classes.timelineContainerEpub}
      />
    </>
  );
});

PubhubReader.propTypes = {
  queue: queuePropType,
  ebookData: ebookDataPropType,
  activeIndex: PropTypes.number,
  resetPlayerAction: PropTypes.func.isRequired,
  updateEBookDataAction: PropTypes.func.isRequired,
  changeEbookReaderOpenAction: PropTypes.func.isRequired,
};

export default PubhubReader;
